import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DEPENDENCY_TYPES, MAX_LAG_DAYS, type DependencyType } from '../../lib/constants'

/** One rendered predecessor/successor row: the mutable dependency fields plus a resolved peer label. */
export interface EdgeRowData { depId: string; peerId: string; peerScheduleId: string; peerLabel: string; isForeign: boolean; type: DependencyType; lagDays: number }

interface Props {
  row: EdgeRowData
  canEdit: boolean
  pending: boolean
  onNavigate: () => void
  onChangeType: (t: DependencyType) => void
  onChangeLag: (n: number) => void
  onDelete: () => void
}

/**
 * One existing predecessor/successor row in `DependenciesSection` (Task 17 fix round 1: split out
 * of that file — review finding #8). Keyed by `row.depId` in the parent's `.map()`, so React already
 * remounts/resets this component's own local `lag` state whenever the dependency set changes —
 * unlike `AddLinkRow`, which needed an explicit fix (review finding #1) since nothing about it
 * changed identity when the focused activity did.
 */
const EdgeRow: React.FC<Props> = ({ row, canEdit, pending, onNavigate, onChangeType, onChangeLag, onDelete }) => {
  const { t } = useTranslation()
  const [lag, setLag] = useState(String(row.lagDays))
  useEffect(() => setLag(String(row.lagDays)), [row.depId, row.lagDays])
  const [lagError, setLagError] = useState<string | null>(null)

  const commitLag = () => {
    const n = Number(lag)
    if (!Number.isInteger(n) || Math.abs(n) > MAX_LAG_DAYS) {
      setLagError(t('Schedule_lag_invalid'))
      setLag(String(row.lagDays))
      return
    }
    setLagError(null)
    if (n !== row.lagDays) onChangeLag(n)
  }

  return (
    <div>
      <div className="flex items-center gap-1 py-0.5">
        <button type="button" className="flex-1 min-w-0 text-left text-sm truncate hover:underline" onClick={onNavigate}>
          {row.isForeign && <span className="mr-1">⧉</span>}{row.peerLabel}
        </button>
        <Select value={row.type} disabled={!canEdit || pending} onValueChange={(v) => onChangeType(v as DependencyType)}>
          <SelectTrigger className="h-7 w-[60px] text-xs px-1.5" title={t(`Schedule_dependency_${row.type}`)}><SelectValue /></SelectTrigger>
          <SelectContent>
            {DEPENDENCY_TYPES.map((dt) => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          className="h-7 w-[48px] text-xs px-1.5" type="number" value={lag} disabled={!canEdit || pending} aria-label={t('Schedule_lag')}
          onChange={(e) => { setLag(e.target.value); if (lagError) setLagError(null) }} onBlur={commitLag}
        />
        {pending
          ? <LoaderCircle className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
          : canEdit && (
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onDelete} aria-label={t('Schedule_op_delete')}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
      </div>
      {lagError && <p className="text-xs text-destructive">{lagError}</p>}
    </div>
  )
}

export default EdgeRow
